import API from "../api";

// ─────────────────────────────────────────────────────────────────────────────
// Coach Revenue Report
// ─────────────────────────────────────────────────────────────────────────────

/** Build a download URL for coach-revenue export (PDF or CSV). */
export const coachRevenueExportUrl = ({
  role = "admin",
  from,
  to,
  branchId,
  coachId,
  format = "pdf",
}) => {
  const params = new URLSearchParams({ from, to, format });
  if (branchId) params.append("branchId", branchId);
  if (coachId) params.append("coachId", coachId);
  return `/api/${role}/reports/coach-revenue/export?${params.toString()}`;
};

/** Trigger coach-revenue PDF export — returns a Blob response. */
export const exportCoachRevenueReport = ({
  role = "admin",
  from,
  to,
  branchId,
  coachId,
  format = "pdf",
}) => {
  const params = { from, to, format };
  if (branchId) params.branchId = branchId;
  if (coachId) params.coachId = coachId;
  return API.get(`/${role}/reports/coach-revenue/export`, {
    params,
    responseType: "blob",
  });
};

/** Email coach-revenue report. */
export const emailCoachRevenueReport = ({
  role = "admin",
  from,
  to,
  branchId,
  coachId,
  format = "pdf",
  toEmail,
}) => {
  const params = { from, to, format, toEmail };
  if (branchId) params.branchId = branchId;
  if (coachId) params.coachId = coachId;
  return API.post(`/${role}/reports/coach-revenue/email`, null, { params });
};

// ─────────────────────────────────────────────────────────────────────────────
// Academy Revenue Summary (Admin only)
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch academy revenue summary JSON data. */
export const fetchAcademyRevenueSummary = ({ from, to, branchId } = {}) => {
  const params = { from, to };
  if (branchId) params.branchId = branchId;
  return API.get("/admin/reports/academy-revenue-summary", { params });
};

/** Export academy revenue summary as PDF or CSV — returns a Blob response. */
export const exportAcademyRevenueSummary = ({
  from,
  to,
  branchId,
  format = "pdf",
}) => {
  const params = { from, to, format };
  if (branchId) params.branchId = branchId;
  return API.get("/admin/reports/academy-revenue-summary/export", {
    params,
    responseType: "blob",
  });
};

/** Email academy revenue summary report. */
export const emailAcademyRevenueSummary = ({
  from,
  to,
  branchId,
  format = "pdf",
  toEmail,
}) => {
  const params = { from, to, format, toEmail };
  if (branchId) params.branchId = branchId;
  return API.post("/admin/reports/academy-revenue-summary/email", null, {
    params,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Student Attendance Summary (Admin + Manager)
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch student attendance summary JSON data. */
export const fetchStudentAttendanceSummary = ({
  role = "admin",
  from,
  to,
  branchId,
  levelId,
  batchId,
  coachId,
} = {}) => {
  const params = { from, to };
  if (branchId) params.branchId = branchId;
  if (levelId) params.levelId = levelId;
  if (batchId) params.batchId = batchId;
  if (coachId) params.coachId = coachId;
  return API.get(`/${role}/reports/student-attendance-summary`, { params });
};

/** Export student attendance summary as PDF or CSV — returns a Blob response. */
export const exportStudentAttendanceSummary = ({
  role = "admin",
  from,
  to,
  branchId,
  levelId,
  batchId,
  coachId,
  format = "pdf",
}) => {
  const params = { from, to, format };
  if (branchId) params.branchId = branchId;
  if (levelId) params.levelId = levelId;
  if (batchId) params.batchId = batchId;
  if (coachId) params.coachId = coachId;
  return API.get(`/${role}/reports/student-attendance-summary/export`, {
    params,
    responseType: "blob",
  });
};

/** Email student attendance summary report. */
export const emailStudentAttendanceSummary = ({
  role = "admin",
  from,
  to,
  branchId,
  levelId,
  batchId,
  coachId,
  format = "pdf",
  toEmail,
}) => {
  const params = { from, to, format, toEmail };
  if (branchId) params.branchId = branchId;
  if (levelId) params.levelId = levelId;
  if (batchId) params.batchId = batchId;
  if (coachId) params.coachId = coachId;
  return API.post(`/${role}/reports/student-attendance-summary/email`, null, {
    params,
  });
};

// ─────────────────────────────────────────────────────────────────────────────
// Payments Due Report (Admin only)
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch payments due JSON data. */
export const fetchPaymentsDueReport = ({
  statusFilter,
  thresholdOverride,
  branchId,
} = {}) => {
  const params = {};
  if (statusFilter) params.statusFilter = statusFilter;
  if (thresholdOverride !== undefined && thresholdOverride !== "")
    params.thresholdOverride = thresholdOverride;
  if (branchId) params.branchId = branchId;
  return API.get("/admin/reports/payments-due", { params });
};

/** Export payments due report as PDF or CSV — returns a Blob response. */
export const exportPaymentsDueReport = ({
  statusFilter,
  thresholdOverride,
  branchId,
  format = "pdf",
}) => {
  const params = { format };
  if (statusFilter) params.statusFilter = statusFilter;
  if (thresholdOverride !== undefined && thresholdOverride !== "")
    params.thresholdOverride = thresholdOverride;
  if (branchId) params.branchId = branchId;
  return API.get("/admin/reports/payments-due/export", {
    params,
    responseType: "blob",
  });
};

/** Email payments due report. */
export const emailPaymentsDueReport = ({
  statusFilter,
  thresholdOverride,
  branchId,
  format = "pdf",
  toEmail,
}) => {
  const params = { format, toEmail };
  if (statusFilter) params.statusFilter = statusFilter;
  if (thresholdOverride !== undefined && thresholdOverride !== "")
    params.thresholdOverride = thresholdOverride;
  if (branchId) params.branchId = branchId;
  return API.post("/admin/reports/payments-due/email", null, { params });
};
